import {
  DefinedUseQueryResult,
  QueryClient,
  useQuery,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  Asset,
  BranchInformation,
  BucketDefinition,
  EditorConfiguration,
  EditorValidationErrors,
  LineAsset,
  LineDefinition,
  LineTestDefinition,
  LocaleAsset,
  LocaleDefinition, OptionDefinition,
  OptionListDefinition,
  OptionListDefinitionV2,
  PriorEnquiryDefinition,
  QuestionAsset,
  QuestionDefinition,
  TableDefinition,
  TestRunStatus,
  WrapUpLine,
} from '../../dtos/editor.dto';
import {
  APIErrorResponse,
  DataRequestParams,
  isAPIError,
  Paginated,
  QueryOptions,
  QueryResult,
  RequiredKey,
  useAPIMutation,
} from '../types';
import { getEditorAPI } from './api';
import {
  AddLineTest,
  AddLineTestParams,
  AddTable,
  AddTableParams,
  CreateBucket,
  CreateBucketParams,
  CreateLine,
  CreateLineParams,
  CreateLocale,
  CreateLocaleParams,
  CreateOptionList,
  CreateOptionListParams,
  CreatePriorEnquiry,
  CreatePriorEnquiryParams,
  CreateQuestion,
  CreateQuestionParams,
  DeleteBucket,
  DeleteBucketParams,
  DeleteLine,
  DeleteLineParams,
  DeleteLineTest,
  DeleteLineTestParams,
  DeleteLocale,
  DeleteLocaleParams,
  DeleteOptionList,
  DeleteOptionListParams,
  DeletePriorEnquiry,
  DeletePriorEnquiryParams,
  DeleteQuestion,
  DeleteQuestionParams,
  DeleteTable,
  DeleteTableParams,
  ExportTemplateType,
  GetBranchTagParams,
  GetBucketParams,
  GetLineParams,
  GetLineTablesParams,
  GetLineTestsParams,
  GetLocaleParams,
  GetOptionListOptionsParams,
  GetOptionListParams,
  GetPriorEnquiryParams,
  GetQuestionParams,
  GetQuestionsParams,
  ImportForEachTests,
  ImportOptionList,
  ImportOptionListParams,
  RunLineTest,
  RunLineTestParams,
  TypeAheadBucketValueCompleter,
  TypeAheadContributionBucketCompleter,
  UpdateBucket,
  UpdateBucketParams,
  UpdateLine,
  UpdateLineParams,
  UpdateLocale,
  UpdateLocaleParams,
  UpdateOptionList,
  UpdateOptionListParams,
  UpdatePriorEnquiry,
  UpdatePriorEnquiryParams,
  UpdateQuestion,
  UpdateQuestionParams,
  UpdateWrapUpLinesOrder,
  UpdateWrapUpLinesOrderParams,
  ValidateBranch,
  ValidateBranchParams,
  ValidateLine,
  ValidateLineParams,
} from './api/types';
import { handleDownloadApiError } from '../../utils/excel-api';
import { EditorQueryKeys } from './editorQueryKeys';
import { download } from '../../utils/download';

const API = getEditorAPI();

/**
 * Add new query keys here for cache refresh upon import
 */
const allQueryKeys = [
  EditorQueryKeys.BranchInfo,
  EditorQueryKeys.LinesList,
  EditorQueryKeys.BucketsList,
  EditorQueryKeys.OptionListsList,
  EditorQueryKeys.QuestionsList,
  EditorQueryKeys.LocalesList,
  EditorQueryKeys.PriorEnquiryChannelList,
  EditorQueryKeys.BucketDef,
  EditorQueryKeys.LocaleDef,
  EditorQueryKeys.QuestionDef,
  EditorQueryKeys.OptionListDef,
  EditorQueryKeys.OptionListDefV2,
  EditorQueryKeys.OptionListOptions,
  EditorQueryKeys.OptionListOptionsDef,
  EditorQueryKeys.PriorEnquiryDef,
  EditorQueryKeys.LineDef,
  EditorQueryKeys.WrapUpLineOrderDef,
  EditorQueryKeys.GraphLineTables,
  EditorQueryKeys.GraphBranchTables,
  EditorQueryKeys.GraphQuestions,
  EditorQueryKeys.LineTests,
  EditorQueryKeys.ValidationErrors,
  EditorQueryKeys.TypeAheadBucketValue,
  EditorQueryKeys.TypeAheadContributionBucket,
];

export const clearEditorCaches = (queryClient: QueryClient, branchName: string) => {
  allQueryKeys.forEach((key) => queryClient.invalidateQueries([key, branchName]));
};

const branchInfoKey = (branchName: RequiredKey) => [EditorQueryKeys.BranchInfo, branchName];
const editorConfigurationKey = () => [EditorQueryKeys.EditorConfiguration];
const linesListKey = (branchName: RequiredKey, tagName?: RequiredKey) => [EditorQueryKeys.LinesList, branchName, tagName];
const bucketsListKey = (branchName: RequiredKey, tagName?: RequiredKey) => [EditorQueryKeys.BucketsList, branchName, tagName];
const optionListsListKey = (branchName: RequiredKey, tagName?: RequiredKey) => [EditorQueryKeys.OptionListsList, branchName, tagName];
const optionListOptionsPrefixKey = (branchName: RequiredKey, optionListName: RequiredKey, tagName?: RequiredKey) => [EditorQueryKeys.OptionListOptions, branchName, tagName, optionListName];
const optionListOptionsDefPrefixKey = (branchName: RequiredKey, optionListName: RequiredKey) => [EditorQueryKeys.OptionListOptionsDef, branchName, optionListName];
const questionListKey = (branchName: RequiredKey, tagName?: RequiredKey) => [EditorQueryKeys.QuestionsList, branchName, tagName];
const effectiveLocalesListKey = (branchName: RequiredKey, tagName?: RequiredKey) => [EditorQueryKeys.EffectiveLocalesList, branchName, tagName];
const localesListKey = (branchName: RequiredKey, tagName?: RequiredKey) => [EditorQueryKeys.LocalesList, branchName, tagName];
const priorEnquiryChannelListKey = (branchName: RequiredKey, tagName?: RequiredKey) => [EditorQueryKeys.PriorEnquiryChannelList, branchName, tagName];
const bucketDefKey = (branchName: RequiredKey, bucketName: RequiredKey, tagName?: RequiredKey) => [EditorQueryKeys.BucketDef, branchName, tagName, bucketName];
const localeDefKey = (branchName: RequiredKey, localeName: RequiredKey, tagName?: RequiredKey) => [EditorQueryKeys.LocaleDef, branchName, tagName, localeName];
const questionDefKey = (branchName: RequiredKey, questionName: RequiredKey, tagName?: RequiredKey) => [EditorQueryKeys.QuestionDef, branchName, tagName, questionName];
const graphQuestionsKey = (branchName: RequiredKey, questionNames: string[], tagName?: RequiredKey) => [EditorQueryKeys.GraphQuestions, branchName, tagName, questionNames.sort().join(',')];
const optionListDefKey = (branchName: RequiredKey, optionListName: RequiredKey, tagName?: RequiredKey) => [EditorQueryKeys.OptionListDef, branchName, tagName, optionListName];
const optionListDefV2Key = (branchName: RequiredKey, optionListName: RequiredKey, tagName?: RequiredKey) => [EditorQueryKeys.OptionListDefV2, branchName, tagName, optionListName];
const priorEnquiryDefKey = (branchName: RequiredKey, priorEnquiryName: RequiredKey, tagName?: RequiredKey) => [EditorQueryKeys.PriorEnquiryDef, branchName, tagName, priorEnquiryName];
const lineDefKey = (branchName: RequiredKey, lineName: RequiredKey, tagName?: RequiredKey) => [EditorQueryKeys.LineDef, branchName, tagName, lineName];
const wrapUpLinesOrderKey = (branchName: RequiredKey, tagName?: RequiredKey) => [EditorQueryKeys.WrapUpLineOrderDef, branchName, tagName];
const lineTablesKey = (branchName: RequiredKey, lineName: RequiredKey, tagName?: RequiredKey) => [EditorQueryKeys.GraphLineTables, branchName, tagName, lineName];
const branchTablesKey = (branchName: RequiredKey, tagName?: RequiredKey) => [EditorQueryKeys.GraphBranchTables, branchName, tagName];
const lineTestsKey = (branchName: RequiredKey, lineName: RequiredKey, tagName?: RequiredKey) => [EditorQueryKeys.LineTests, branchName, tagName, lineName];
const validationErrorsKey = (branchName: RequiredKey) => [EditorQueryKeys.ValidationErrors, branchName];

// eslint-disable-next-line max-len
export const useBranchInfo = (branchName: RequiredKey, options?: QueryOptions<BranchInformation>): QueryResult<BranchInformation> => useQuery(
  branchInfoKey(branchName),
  async (): Promise<BranchInformation> => API.getBranchInfo.call(branchName as string),
  {
    enabled: !!branchName,
    ...options,
  },
);

export const useEditorConfiguration = (options?: QueryOptions<EditorConfiguration, any>): QueryResult<EditorConfiguration> => useQuery(
  editorConfigurationKey(),
  async (): Promise<EditorConfiguration> => API.editorConfiguration.call(),
  options,
);

const registerGetLinesList = (queryClient: QueryClient) => queryClient.setQueryDefaults([EditorQueryKeys.LinesList], {
  queryFn: async (
    { queryKey: [, branchId, tagName] },
  ) => API.getLinesList.call({ branchName: branchId as string, tagName: tagName as string | undefined }),
});
export const useLinesList = (
  branchId: RequiredKey,
  tagName?: string,
  options?: QueryOptions<LineAsset[]>,
): QueryResult<LineAsset[]> => useQuery(
  linesListKey(branchId, tagName),
  {
    enabled: !!branchId,
    ...options,
  },
);

const registerGetBucketsList = (queryClient: QueryClient) => queryClient.setQueryDefaults([EditorQueryKeys.BucketsList], {
  queryFn: async (
    { queryKey: [, branchId, tagName] },
  ) => API.getBucketsList.call({ branchName: branchId as string, tagName: tagName as string | undefined }),
});
export const useBucketList = (branchId: RequiredKey, tagName?: string, options?: QueryOptions<Asset[]>): QueryResult<Asset[]> => useQuery(
  bucketsListKey(branchId, tagName),
  {
    enabled: !!branchId,
    ...options,
  },
);

const registerGetOptionsLists = (queryClient: QueryClient) => queryClient.setQueryDefaults([EditorQueryKeys.OptionListsList], {
  queryFn: async (
    { queryKey: [, branchId, tagName] },
  ) => API.getOptionListList.call({ branchName: branchId as string, tagName: tagName as string | undefined }),
});
export const useOptionLists = (
  branchId: RequiredKey,
  tagName?: RequiredKey,
  options?: QueryOptions<Asset[]>,
): QueryResult<Asset[]> => useQuery(
  optionListsListKey(branchId, tagName),
  {
    enabled: !!branchId,
    ...options,
  },
);

const registerGetPaginatedOptionListOptions = (queryClient: QueryClient) => queryClient
  .setQueryDefaults([EditorQueryKeys.OptionListOptions], {
    queryFn: async ({ queryKey: [, branchName, tagName, optionListName, filterText, params] }): Promise<Paginated<OptionDefinition>> => API
      .getOptionListOptions.call({
        branchName: branchName as string,
        tagName: tagName as string | undefined,
        optionListName: optionListName as string,
        filterText: filterText as string | undefined,
        params: params as DataRequestParams,
      } as GetOptionListOptionsParams),
  });

const registerGetQuestionsList = (queryClient: QueryClient) => queryClient.setQueryDefaults([EditorQueryKeys.QuestionsList], {
  queryFn: async (
    { queryKey: [, branchId, tagName] },
  ) => API.getQuestionsList.call({ branchName: branchId as string, tagName: tagName as string | undefined }),
});
export const useQuestionList = (
  branchId: RequiredKey,
  tagName?: RequiredKey,
  options?: QueryOptions<QuestionAsset[]>,
): QueryResult<QuestionAsset[]> => useQuery(
  questionListKey(branchId, tagName),
  {
    enabled: !!branchId,
    ...options,
  },
);

const registerGetEffectiveLocalesList = (queryClient: QueryClient) => queryClient.setQueryDefaults([EditorQueryKeys.EffectiveLocalesList], {
  queryFn: async ({ queryKey: [, branchName, tagName] }) => API.getLocalesList.call({
    branchName: branchName as string,
    tagName: tagName as string | undefined,
    includeDefaultLocale: true,
  }),
});
export const useEffectiveLocalesList = (
  branchName: RequiredKey,
  tagName?: RequiredKey,
  options?: QueryOptions<LocaleAsset[]>,
): QueryResult<LocaleAsset[]> => useQuery(
  effectiveLocalesListKey(branchName, tagName),
  {
    enabled: !!branchName,
    select: (locales) => locales.sort((a) => (a.isDefault ? -1 : 0)),
    ...options,
  },
);

const registerGetLocalesList = (queryClient: QueryClient) => queryClient.setQueryDefaults([EditorQueryKeys.LocalesList], {
  queryFn: async ({ queryKey: [, branchName, tagName] }) => API.getLocalesList.call({
    branchName: branchName as string,
    tagName: tagName as string | undefined,
    includeDefaultLocale: false,
  }),
});
export const useLocalesList = (
  branchName: RequiredKey,
  tagName?: RequiredKey,
  options?: QueryOptions<LocaleAsset[]>,
): QueryResult<LocaleAsset[]> => useQuery(
  localesListKey(branchName, tagName),
  {
    enabled: !!branchName,
    select: (locales) => locales.sort((a) => (a.isDefault ? -1 : 0)),
    ...options,
  },
);

// eslint-disable-next-line max-len
const registerGetPriorEnquiryChannelList = (queryClient: QueryClient) => queryClient.setQueryDefaults([EditorQueryKeys.PriorEnquiryChannelList], {
  queryFn: async (
    { queryKey: [, branchId, tagName] },
  ) => API.getPriorEnquiryChannelList.call({ branchName: branchId as string, tagName: tagName as string | undefined }),
});
export const usePriorEnquiryChannelList = (
  branchId: RequiredKey,
  tagName?: RequiredKey,
  options?: QueryOptions<Asset[]>,
): QueryResult<Asset[]> => useQuery(
  priorEnquiryChannelListKey(branchId, tagName),
  {
    enabled: !!branchId,
    ...options,
  },
);

export const useBucket = (
  branchName: RequiredKey,
  bucketName: RequiredKey,
  tagName?: RequiredKey,
  options?: QueryOptions<BucketDefinition>,
): QueryResult<BucketDefinition> => useQuery(
  bucketDefKey(branchName, bucketName, tagName),
  async (): Promise<BucketDefinition> => API.getBucket.call({ branchName, tagName, bucketName } as GetBucketParams),
  {
    enabled: !!branchName && !!bucketName,
    ...options,
  },
);

export const useUpdateBucketMutation = useAPIMutation<UpdateBucket>(EditorQueryKeys.UpdateBucket);
const registerUpdateBucket = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.UpdateBucket], {
  mutationFn: API.updateBucket.call,
  onSuccess: (_, { branchName, bucketName, bucket }: UpdateBucketParams) => {
    if (bucket.name !== bucketName) { // rename
      clearEditorCaches(queryClient, branchName);
    } else { // update existing bucket
      queryClient.removeQueries(bucketDefKey(branchName, bucketName));
    }
  },
});

export const useCreateBucketMutation = useAPIMutation<CreateBucket>(EditorQueryKeys.CreateBucket);
const registerCreateBucket = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.CreateBucket], {
  mutationFn: API.createBucket.call,
  onSuccess: (_, { branchName }: CreateBucketParams) => {
    queryClient.invalidateQueries(bucketsListKey(branchName));
  },
});

export const useDeleteBucketMutation = useAPIMutation<DeleteBucket>(EditorQueryKeys.DeleteBucket);
const registerDeleteBucket = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.DeleteBucket], {
  mutationFn: API.deleteBucket.call,
  onSuccess: (_, { branchName, bucketName }: DeleteBucketParams) => {
    queryClient.removeQueries(bucketDefKey(branchName, bucketName));
    queryClient.invalidateQueries(bucketsListKey(branchName));
  },
  onError: (e, { branchName }: DeleteBucketParams) => {
    if (isAPIError(e)) {
      const errorResponse = e.response?.data || {} as APIErrorResponse;
      if (!errorResponse.validations?.length) {
        if (errorResponse.error === 'validation.error.bucket.missing') {
          queryClient.invalidateQueries(bucketsListKey(branchName));
        }
      }
    }
  },
});

export const useLocale = (
  branchName: RequiredKey,
  localeName: RequiredKey,
  tagName?: RequiredKey,
  options?: QueryOptions<LocaleDefinition>,
): QueryResult<LocaleDefinition> => useQuery(
  localeDefKey(branchName, localeName, tagName),
  async (): Promise<LocaleDefinition> => API.getLocale.call({ branchName, tagName, localeName } as GetLocaleParams),
  {
    enabled: !!branchName && !!localeName,
    ...options,
  },
);

export const useUpdateLocaleMutation = useAPIMutation<UpdateLocale>(EditorQueryKeys.UpdateLocale);
const registerUpdateLocale = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.UpdateLocale], {
  mutationFn: API.updateLocale.call,
  onSuccess: (_, { branchName, localeName, locale }: UpdateLocaleParams) => {
    if (locale.name !== localeName) { // rename
      clearEditorCaches(queryClient, branchName);
    } else {
      queryClient.removeQueries(localeDefKey(branchName, localeName));
    }
  },
});

export const useCreateLocaleMutation = useAPIMutation<CreateLocale>(EditorQueryKeys.CreateLocale);
const registerCreateLocale = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.CreateLocale], {
  mutationFn: API.createLocale.call,
  onSuccess: (_, { branchName }: CreateLocaleParams) => {
    queryClient.invalidateQueries(localesListKey(branchName));
  },
});

export const useDeleteLocaleMutation = useAPIMutation<DeleteLocale>(EditorQueryKeys.DeleteLocale);
const registerDeleteLocale = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.DeleteLocale], {
  mutationFn: API.deleteLocale.call,
  onSuccess: (_, { branchName, localeName }: DeleteLocaleParams) => {
    queryClient.removeQueries(localeDefKey(branchName, localeName));
    queryClient.invalidateQueries(localesListKey(branchName));
  },
});

export const useQuestion = (
  branchId: RequiredKey,
  questionName: RequiredKey,
  tagName?: RequiredKey,
  options?: QueryOptions<QuestionDefinition>,
): QueryResult<QuestionDefinition> => useQuery(
  questionDefKey(branchId, questionName, tagName),
  async (): Promise<QuestionDefinition> => API.getQuestion.call({ branchName: branchId, tagName, questionName } as GetQuestionParams),
  {
    enabled: !!branchId && !!questionName,
    ...options,
  },
);

export const useQuestions = (
  branchName: RequiredKey,
  questionNames: string[],
  tagName?: RequiredKey,
  options?: QueryOptions<QuestionDefinition[]>,
): QueryResult<QuestionDefinition[]> => useQuery(
  graphQuestionsKey(branchName, questionNames, tagName),
  async (): Promise<QuestionDefinition[]> => API.searchQuestions.call({ branchName, tagName, questionNames } as GetQuestionsParams),
  {
    enabled: !!branchName && !!questionNames.length,
    ...options,
  },
);

export const useUpdateQuestionMutation = useAPIMutation<UpdateQuestion>(EditorQueryKeys.UpdateQuestion);
const registerUpdateQuestion = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.UpdateQuestion], {
  mutationFn: API.updateQuestion.call,
  onSuccess: (_, { branchName }: UpdateQuestionParams) => {
    clearEditorCaches(queryClient, branchName);
  },
});

export const useCreateQuestionMutation = useAPIMutation<CreateQuestion>(EditorQueryKeys.CreateQuestion);
const registerCreateQuestion = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.CreateQuestion], {
  mutationFn: API.createQuestion.call,
  onSuccess: (_, { branchName }: CreateQuestionParams) => {
    queryClient.invalidateQueries(questionListKey(branchName));
  },
});

export const useDeleteQuestionMutation = useAPIMutation<DeleteQuestion>(EditorQueryKeys.DeleteQuestion);
const registerDeleteQuestion = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.DeleteQuestion], {
  mutationFn: API.deleteQuestion.call,
  onSuccess: (_, { branchName, questionName }: DeleteQuestionParams) => {
    queryClient.invalidateQueries(questionListKey(branchName));
    queryClient.removeQueries(questionDefKey(branchName, questionName));
  },
  onError: (e, { branchName }: DeleteQuestionParams) => {
    if (isAPIError(e)) {
      const errorResponse = e.response?.data || {} as APIErrorResponse;
      if (!errorResponse.validations?.length) {
        if (errorResponse.error === 'validation.error.question.missing') {
          queryClient.invalidateQueries(questionListKey(branchName));
        }
      }
    }
  },
});

export const useOptionList = (
  branchId: RequiredKey,
  optionListName: RequiredKey,
  tagName?: RequiredKey,
  options?: QueryOptions<OptionListDefinition>,
): QueryResult<OptionListDefinition> => useQuery(
  optionListDefKey(branchId, optionListName, tagName),
  async () => API.getOptionList.call({ branchName: branchId, tagName, optionListName } as GetOptionListParams).then(),
  {
    enabled: !!branchId && !!optionListName,
    ...options,
  },
);

export const useOptionListV2 = (
  branchName: RequiredKey,
  optionListName: RequiredKey,
  tagName?: RequiredKey,
  options?: QueryOptions<OptionListDefinitionV2>,
): QueryResult<OptionListDefinitionV2> => useQuery(
  optionListDefV2Key(branchName, optionListName, tagName),
  async () => API.getOptionListV2.call({ branchName, tagName, optionListName } as GetOptionListParams).then(),
  {
    enabled: !!branchName && !!optionListName,
    ...options,
  },
);

export const useUpdateOptionListMutation = useAPIMutation<UpdateOptionList>(EditorQueryKeys.UpdateOptionList);
const registerUpdateOptionList = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.UpdateOptionList], {
  mutationFn: API.updateOptionList.call,
  onSuccess: (_, { branchName, optionListName, optionList }: UpdateOptionListParams) => {
    if (optionList.name !== optionListName) { // rename
      clearEditorCaches(queryClient, branchName);
    } else {
      queryClient.removeQueries(optionListDefKey(branchName, optionListName));
      queryClient.removeQueries(optionListDefV2Key(branchName, optionListName));
    }
  },
});

export const useCreateOptionListMutation = useAPIMutation<CreateOptionList>(EditorQueryKeys.CreateOptionList);
const registerCreateOptionList = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.CreateOptionList], {
  mutationFn: API.createOptionList.call,
  onSuccess: (_, { branchName }: CreateOptionListParams) => {
    queryClient.invalidateQueries(optionListsListKey(branchName)).then();
  },
});

export const useDeleteOptionListMutation = useAPIMutation<DeleteOptionList>(EditorQueryKeys.DeleteOptionList);
const registerDeleteOptionList = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.DeleteOptionList], {
  mutationFn: API.deleteOptionList.call,
  onSuccess: (_, { branchName, optionListName }: DeleteOptionListParams) => {
    queryClient.removeQueries(optionListDefKey(branchName, optionListName));
    queryClient.removeQueries(optionListDefV2Key(branchName, optionListName));
    queryClient.invalidateQueries(optionListsListKey(branchName)).then();
    queryClient.invalidateQueries(optionListOptionsDefPrefixKey(branchName, optionListName)).then();
    queryClient.invalidateQueries(optionListOptionsPrefixKey(branchName, optionListName)).then();
  },
});

/**
 * @Deprecated - this has been left in for now... it is used to proxy a call for Paginated OptionDefinition belonging
 * to an OptionList. This was used to populate a drop-drown for option backed buckets. The auto-completion functionality
 * once integrated should return the values as any expression can be used to contribute to a bucket.
 * @param queryClient - to initialise the default query on
 */
// eslint-disable-next-line max-len
const registerGetOptionListOptions = (queryClient: QueryClient) => queryClient.setQueryDefaults([EditorQueryKeys.OptionListOptionsDef], {
  queryFn: async ({ queryKey: [, branchId, optionListName, params], pageParam }): Promise<Paginated<OptionDefinition>> => API
    .getOptionList.call({
      branchName: branchId,
      optionListName,
      params: { ...params as DataRequestParams, ...pageParam },
    } as GetOptionListParams).then((optionListDefinition) => ({
      items: optionListDefinition?.options ? optionListDefinition?.options : [],
      total: optionListDefinition?.options.length,
      limit: optionListDefinition?.options.length,
    } as Paginated<OptionDefinition>)),
});

export const exportOptionList = download(API.exportOptionList.call, ({ optionListName }) => `${optionListName}.xlsx`);

export const useImportOptionListMutation = useAPIMutation<ImportOptionList>(EditorQueryKeys.ImportOptionList);
const registerImportOptionList = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.ImportOptionList], {
  mutationFn: API.importOptionList.call,
  onSuccess: (createdOptionList, { branchName }: ImportOptionListParams) => {
    queryClient.invalidateQueries(optionListsListKey(branchName)).then();
    queryClient.invalidateQueries(optionListDefKey(branchName, createdOptionList.name)).then();
    queryClient.invalidateQueries(optionListDefV2Key(branchName, createdOptionList.name)).then();
    queryClient.invalidateQueries(optionListOptionsDefPrefixKey(branchName, createdOptionList.name)).then();
    queryClient.invalidateQueries(optionListOptionsPrefixKey(branchName, createdOptionList.name)).then();
  },
});

export const usePriorEnquiry = (
  branchName: RequiredKey,
  priorEnquiryName: RequiredKey,
  tagName?: RequiredKey,
  options?: QueryOptions<PriorEnquiryDefinition>,
): QueryResult<PriorEnquiryDefinition> => useQuery(
  priorEnquiryDefKey(branchName, priorEnquiryName, tagName),
  async () => API.getPriorEnquiry.call({ branchName, tagName, priorEnquiryName } as GetPriorEnquiryParams),
  {
    enabled: !!branchName && !!priorEnquiryName,
    ...options,
  },
);

export const useUpdatePriorEnquiryMutation = useAPIMutation<UpdatePriorEnquiry>(EditorQueryKeys.UpdatePriorEnquiry);
const registerUpdatePriorEnquiry = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.UpdatePriorEnquiry], {
  mutationFn: API.updatePriorEnquiry.call,
  onSuccess: (_, { branchName, priorEnquiryName }: UpdatePriorEnquiryParams) => {
    queryClient.removeQueries(priorEnquiryDefKey(branchName, priorEnquiryName));
    queryClient.invalidateQueries(priorEnquiryChannelListKey(branchName));
  },
});

export const useCreatePriorEnquiryMutation = useAPIMutation<CreatePriorEnquiry>(EditorQueryKeys.CreatePriorEnquiry);
const registerCreatePriorEnquiry = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.CreatePriorEnquiry], {
  mutationFn: API.createPriorEnquiry.call,
  onSuccess: (_, { branchName }: CreatePriorEnquiryParams) => {
    queryClient.invalidateQueries(priorEnquiryChannelListKey(branchName));
  },
});

export const useDeletePriorEnquiryMutation = useAPIMutation<DeletePriorEnquiry>(EditorQueryKeys.DeletePriorEnquiry);
const registerDeletePriorEnquiry = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.DeletePriorEnquiry], {
  mutationFn: API.deletePriorEnquiry.call,
  onSuccess: (_, { branchName, priorEnquiryName }: DeletePriorEnquiryParams) => {
    queryClient.removeQueries(priorEnquiryDefKey(branchName, priorEnquiryName));
    queryClient.invalidateQueries(priorEnquiryChannelListKey(branchName));
  },
  onError: (e, { branchName }: DeletePriorEnquiryParams) => {
    if (isAPIError(e)) {
      const errorResponse = e.response?.data || {} as APIErrorResponse;
      if (!errorResponse.validations?.length) {
        if (errorResponse.error === 'validation.error.priory-enquiry.missing') {
          queryClient.invalidateQueries(priorEnquiryChannelListKey(branchName));
        }
      }
    }
  },
});

export const useLine = (
  branchName: RequiredKey,
  lineName: RequiredKey,
  tagName?: RequiredKey,
  options?: QueryOptions<LineDefinition>,
): QueryResult<LineDefinition> => useQuery(
  lineDefKey(branchName, lineName, tagName),
  async () => API.getLine.call({ branchName, tagName, lineName } as GetLineParams),
  {
    enabled: !!branchName && !!lineName,
    ...options,
  },
);

export const useUpdateLineMutation = useAPIMutation<UpdateLine>(EditorQueryKeys.UpdateLine);
const registerUpdateLine = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.UpdateLine], {
  mutationFn: API.updateLine.call,
  onSuccess: (_, { branchName }: UpdateLineParams) => {
    clearEditorCaches(queryClient, branchName);
  },
});

export const useCreateLineMutation = useAPIMutation<CreateLine>(EditorQueryKeys.CreateLine);
const registerCreateLine = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.CreateLine], {
  mutationFn: API.createLine.call,
  onSuccess: (_, { branchName }: CreateLineParams) => {
    queryClient.invalidateQueries(linesListKey(branchName)).then();
    queryClient.invalidateQueries(wrapUpLinesOrderKey(branchName)).then();
  },
});

export const useDeleteLineMutation = useAPIMutation<DeleteLine>(EditorQueryKeys.DeleteLine);
const registerDeleteLine = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.DeleteLine], {
  mutationFn: API.deleteLine.call,
  onSuccess: (_, { branchName, lineName }: DeleteLineParams) => {
    queryClient.removeQueries(lineDefKey(branchName, lineName));
    queryClient.invalidateQueries(linesListKey(branchName)).then();
    queryClient.invalidateQueries(wrapUpLinesOrderKey(branchName)).then();
    queryClient.invalidateQueries(lineTablesKey(branchName, lineName)).then();
    queryClient.invalidateQueries(lineTestsKey(branchName, lineName)).then();
  },
});

export const useWrapUpLinesOrder = (
  branchName: RequiredKey,
  tagName?: RequiredKey,
  options?: QueryOptions<WrapUpLine[]>,
): QueryResult<WrapUpLine[]> => useQuery(
  wrapUpLinesOrderKey(branchName, tagName),
  async () => API.getWrapUpLinesOrder.call({ branchName, tagName } as GetBranchTagParams),
  {
    enabled: !!branchName,
    ...options,
  },
);

export const useUpdateWrapUpLinesOrderMutation = useAPIMutation<UpdateWrapUpLinesOrder>(EditorQueryKeys.UpdateWrapUpLinesOrder);
// eslint-disable-next-line max-len
const registerUpdateWrapUpLinesOrder = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.UpdateWrapUpLinesOrder], {
  mutationFn: API.updateWrapUpLinesOrder.call,
  onSuccess: (_, { branchName }: UpdateWrapUpLinesOrderParams) => {
    queryClient.invalidateQueries(wrapUpLinesOrderKey(branchName)).then();
  },
});

// eslint-disable-next-line max-len
export const useLineTables = (branchName: RequiredKey, lineName: RequiredKey, tagName?: RequiredKey, options?: QueryOptions<TableDefinition[]>): QueryResult<TableDefinition[]> => useQuery(
  lineTablesKey(branchName, lineName, tagName),
  async (): Promise<TableDefinition[]> => API.getLineTables.call({ branchName, tagName, lineName } as GetLineTablesParams),
  {
    enabled: !!branchName && !!lineName,
    ...options,
  },
);

// eslint-disable-next-line max-len
export const useBranchTables = (branchName: RequiredKey, tagName?: RequiredKey, options?: QueryOptions<TableDefinition[]>): QueryResult<TableDefinition[]> => useQuery(
  branchTablesKey(branchName, tagName),
  async (): Promise<TableDefinition[]> => API.getBranchTables.call({ branchName, tagName } as GetBranchTagParams),
  {
    enabled: !!branchName,
    ...options,
  },
);

export const useCreateTableMutation = useAPIMutation<AddTable>(EditorQueryKeys.CreateTable);
const registerCreateTable = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.CreateTable], {
  mutationFn: API.addTable.call,
  onSuccess: (_, { branchName, lineName }: AddTableParams) => {
    queryClient.invalidateQueries(lineTablesKey(branchName, lineName));
    queryClient.invalidateQueries(branchTablesKey(branchName));
  },
});

export const useDeleteTableMutation = useAPIMutation<DeleteTable>(EditorQueryKeys.DeleteTable);
const registerDeleteTable = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.DeleteTable], {
  mutationFn: API.deleteTable.call,
  onSuccess: (_, { branchName, lineName }: DeleteTableParams) => {
    queryClient.invalidateQueries(lineTablesKey(branchName, lineName));
    queryClient.invalidateQueries(branchTablesKey(branchName));
  },
});

export const exportTable = download(API.exportTable.call, ({ name }) => `${name}.xlsx`);

// eslint-disable-next-line max-len
export const useLineTests = (branchName: RequiredKey, lineName: RequiredKey, tagName?: RequiredKey, options?: QueryOptions<LineTestDefinition[]>): QueryResult<LineTestDefinition[]> => useQuery(
  lineTestsKey(branchName, lineName, tagName),
  async (): Promise<LineTestDefinition[]> => API.getLineTests.call({ branchName, tagName, lineName } as GetLineTestsParams),
  {
    enabled: !!branchName && !!lineName,
    ...options,
  },
);

export const useCreateLineTestMutation = useAPIMutation<AddLineTest>(EditorQueryKeys.CreateLineTest);
const registerCreateLineTest = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.CreateLineTest], {
  mutationFn: API.addLineTest.call,
  onSuccess: (_, { branchName, lineName }: AddLineTestParams) => {
    queryClient.invalidateQueries(lineTestsKey(branchName, lineName));
  },
  onError: async (error) => handleDownloadApiError(error, 'validation.error.asset.line.test.spreadsheet.errors'),
});

export const useImportForEachTestsMutation = useAPIMutation<ImportForEachTests>(EditorQueryKeys.ImportForEachTests);
const registerImportForEachTests = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.ImportForEachTests], {
  mutationFn: API.importForEachTests.call,
  onSuccess: (_, { branchName, lineName }: AddLineTestParams) => {
    queryClient.invalidateQueries(lineTestsKey(branchName, lineName));
  },
  onError: async (error) => handleDownloadApiError(error, 'error.upload.xlsx.foreach.validation'),
});

export const useDeleteLineTestMutation = useAPIMutation<DeleteLineTest>(EditorQueryKeys.DeleteLineTest);
const registerDeleteLineTest = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.DeleteLineTest], {
  mutationFn: API.deleteLineTest.call,
  onSuccess: (_, { branchName, lineName }: DeleteLineTestParams) => {
    queryClient.invalidateQueries(lineTestsKey(branchName, lineName));
  },
});

export const exportLineTest = download(API.exportLineTest.call, ({ name }) => `${name}.xlsx`);

export const useRunLineTestMutation = useAPIMutation<RunLineTest>(EditorQueryKeys.RunLineTest);
const registerRunLineTest = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.RunLineTest], {
  /*
   Not great that we have to force update the state of the definition like this, but currently there is no "status"
   api for the line test run,so this will allow us to at least notify the user the run is happening, until the request
   completes and the real state is returned.
   */
  mutationFn: (params: RunLineTestParams) => {
    // eslint-disable-next-line max-len
    queryClient.setQueryData(lineTestsKey(params.branchName, params.lineName), (tests: LineTestDefinition[] | undefined) => tests?.map(
      (test) => (test.name === params.name ? ({ ...test, status: TestRunStatus.PENDING }) : test),
    ) || []);
    return API.runLineTest.call(params);
  },
  onSuccess: (_, { branchName, lineName }: DeleteLineTestParams) => {
    queryClient.invalidateQueries(lineTestsKey(branchName, lineName));
  },
});

export const exportTestTemplate = download(API.exportTestTemplate.call, (params) => (params.exportType === ExportTemplateType.BLANK ? 'Template_Blank.xlsx' : 'Template_ForEach.xlsx'));

// eslint-disable-next-line max-len
export const useValidationErrors = (branchId: RequiredKey, options?: QueryOptions<EditorValidationErrors>): DefinedUseQueryResult<undefined | EditorValidationErrors, AxiosError<EditorValidationErrors> | Error> => useQuery(
  validationErrorsKey(branchId),
  {
    enabled: !!branchId,
    staleTime: Infinity,
    cacheTime: Infinity,
    initialData: {},
    ...options,
  },
);

export const useValidateBranchMutation = useAPIMutation<ValidateBranch>(EditorQueryKeys.ValidateBranch);
const registerValidateBranch = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.ValidateBranch], {
  mutationFn: API.validateBranch.call,
  onSuccess: (validationErrors: EditorValidationErrors, { branchName }: ValidateBranchParams) => {
    queryClient.setQueryData(validationErrorsKey(branchName), () => validationErrors);
  },
});

export const useValidateLineMutation = useAPIMutation<ValidateLine>(EditorQueryKeys.ValidateLine);
const registerValidateLine = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.ValidateLine], {
  mutationFn: API.validateLine.call,
  onSuccess: (validationErrors: EditorValidationErrors, { branchName }: ValidateLineParams) => {
    queryClient.setQueryData(validationErrorsKey(branchName), () => validationErrors);
  },
});

export const useTypeAheadBucketValueMutation = useAPIMutation<TypeAheadBucketValueCompleter>(EditorQueryKeys.TypeAheadBucketValue);
// eslint-disable-next-line max-len
const registerTypeAheadBucketValue = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.TypeAheadBucketValue], {
  mutationFn: API.typeaheadBucketValue.call,
});

// eslint-disable-next-line max-len
export const useTypeAheadContributionBucketMutation = useAPIMutation<TypeAheadContributionBucketCompleter>(EditorQueryKeys.TypeAheadContributionBucket);
// eslint-disable-next-line max-len
const registerTypeAheadContributionBucket = (queryClient: QueryClient) => queryClient.setMutationDefaults([EditorQueryKeys.TypeAheadContributionBucket], {
  mutationFn: API.typeaheadContributionBucket.call,
});

export const registerEditorCalls = (queryClient: QueryClient) => {
  const registrations = [
    registerGetLinesList,
    registerGetBucketsList,
    registerGetOptionsLists,
    registerGetQuestionsList,
    registerGetLocalesList,
    registerGetEffectiveLocalesList,
    registerGetPriorEnquiryChannelList,
    registerUpdateBucket,
    registerCreateBucket,
    registerDeleteBucket,
    registerUpdateLocale,
    registerCreateLocale,
    registerDeleteLocale,
    registerUpdateQuestion,
    registerCreateQuestion,
    registerDeleteQuestion,
    registerUpdateOptionList,
    registerCreateOptionList,
    registerDeleteOptionList,
    registerImportOptionList,
    registerGetPaginatedOptionListOptions,
    registerGetOptionListOptions,
    registerUpdatePriorEnquiry,
    registerCreatePriorEnquiry,
    registerDeletePriorEnquiry,
    registerUpdateLine,
    registerCreateLine,
    registerDeleteLine,
    registerUpdateWrapUpLinesOrder,
    registerCreateTable,
    registerDeleteTable,
    registerCreateLineTest,
    registerImportForEachTests,
    registerDeleteLineTest,
    registerRunLineTest,
    registerValidateBranch,
    registerValidateLine,
    registerTypeAheadBucketValue,
    registerTypeAheadContributionBucket,
  ];
  registrations.forEach((register) => register(queryClient));
};
